import { useQuery } from "@tanstack/react-query";

import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  listSkills,
  getSkillUser,
  getSkillRecommendations,
  notificationsByUserTo,
  getSkillVerificationNotifications,
} from "@/graphql/queries";

import { recommendToUser, verifySkill } from "@/graphql/mutations";
import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";
import { useState } from "react";
import { useShowModalResult } from "@/components/modal-result";

interface IPayloadRecommededSkill {
  targetUser?: string;
  purpose?: string;
  type?: string;
  typeID?: string;
}

interface IPayloadVerifySkill {
  purpose?: string;
  targetUserID?: string;
  targetObjectID?: string;
  rating?: number;
}

const fetchSkills = async () => {
  const response: any = await API.graphql(graphqlOperation(listSkills));
  const records: any[] = response?.data?.listSkills?.items ?? [];

  return records;
};

const fetchSkillUser = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(getSkillUser, { id }),
  );
  const records: any[] = response?.data?.getSkillUser?.items ?? [];

  return records;
};

const fetchSkillRecommendations = async () => {
  const response: any = await API.graphql(
    graphqlOperation(getSkillRecommendations),
  );
  const records: any[] = response?.data?.getSkillRecommendations ?? [];

  return records;
};

const fetchSkillsRecommendationFromNotification = async (userId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(notificationsByUserTo, {
      userTo: userId,
      filter: {
        or: [
          {
            referenceType: { eq: "SKILL_RECOMMENDATION" },
            or: [{ status: { eq: "UNREAD" } }, { status: { eq: "READ" } }],
          },
          {
            referenceType: { eq: "SKILL_TRACK_RECOMMENDATION" },
            or: [{ status: { eq: "UNREAD" } }, { status: { eq: "READ" } }],
          },
        ],
        _deleted: { ne: true },
      },
    }),
  );
  const records: any[] = response?.data?.notificationsByUserTo?.items ?? [];

  return sortRecordsByKey(records, "createdAt", false);
};

const fetchSkillsVerifyFromNotification = async (userId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(notificationsByUserTo, {
      userTo: userId,
      filter: {
        referenceType: { eq: "VERIFY_SKILL" },
        or: [{ status: { eq: "UNREAD" } }, { status: { eq: "READ" } }],
        _deleted: { ne: true },
      },
      limit: 1000,
    }),
  );
  const records: any[] = response?.data?.notificationsByUserTo?.items ?? [];

  return sortRecordsByKey(records, "createdAt", false);
};

const fetchSkillVerificationNotificationV2 = async () => {
  const response: any = await API.graphql(
    graphqlOperation(getSkillVerificationNotifications),
  );
  const records: any[] =
    response?.data?.getSkillVerificationNotifications?.data ?? [];

  return sortRecordsByKey(records, "createdAt", false);
};

const useListSkills = () => {
  return useQuery({
    queryKey: ["KEY_LIST_SKILLS"],
    queryFn: () => fetchSkills(),
  });
};

const useSkillUser = (userId: string) => {
  return useQuery({
    queryKey: ["KEY_LIST_SKILLS_USER", userId],
    queryFn: () => fetchSkillUser(userId),
  });
};

const useGetSkillRecommendations = () => {
  return useQuery({
    queryKey: ["KEY_LIST_SKILLS_RECOMMENDATION"],
    queryFn: () => fetchSkillRecommendations(),
  });
};

const useListRecommendedSkillFromNotification = (userId: string) => {
  return useQuery({
    queryKey: ["KEY_LIST_SKILLS_RECOMMED_FROM_NOTIFICATION", userId],
    queryFn: () => fetchSkillsRecommendationFromNotification(userId),
  });
};

const useListVerifySkillFromNotification = (userId: string) => {
  return useQuery({
    queryKey: ["KEY_LIST_SKILLS_VERIFY_FROM_NOTIFICATION", userId],
    queryFn: () => fetchSkillsVerifyFromNotification(userId),
  });
};

const useSkillVerificationNotificationV2 = () => {
  return useQuery({
    queryKey: ["KEY_SKILL_VERIFICATION_NOTIFICATION_V2"],
    queryFn: () => fetchSkillVerificationNotificationV2(),
  });
};

const useUpdateRecommededSkill = async (payload: IPayloadRecommededSkill) => {
  return await API.graphql<GraphQLQuery<any>>({
    query: recommendToUser,
    variables: payload,
  });
};

const useUpdateVerifySkill = async (payload: IPayloadVerifySkill) => {
  return await API.graphql<GraphQLQuery<any>>({
    query: verifySkill,
    variables: payload,
  });
};

const useVerifySkill = () => {
  const [isProcess, isProcessSet] = useState<string | null>(null);
  const { successModal, errorModal } = useShowModalResult();

  const handleAction = async (
    targetUserID: string,
    targetObjectID: string,
    rateValue: number,
    purpose: "ACCEPTED" | "DECLINED",
    message?: string,
  ) => {
    isProcessSet(targetUserID);

    const payload = {
      targetUserID,
      targetObjectID,
      purpose,
      rating: rateValue,
      message,
    };

    try {
      await useUpdateVerifySkill(payload);

      if (purpose === "DECLINED") {
        successModal({
          show: true,
          title: "Skill Verification Declined",
          body: `You have successfully Declined to verify this Skill.`,
        });
      } else {
        successModal({
          show: true,
          title: "Skill Verification Success",
          body: `You have successfully verified this Skill.`,
        });
      }

      isProcessSet(null);
    } catch (error: any) {
      errorModal({
        title: "Error",
        body:
          error?.errors?.[0]?.message ||
          `${purpose?.toLocaleLowerCase()} skill verify failed, please try again`,
        show: true,
        type: "error",
      });

      isProcessSet(null)
    }
  };
  return { handleAction, isProcess };
};

export {
  useListSkills,
  useListRecommendedSkillFromNotification,
  useListVerifySkillFromNotification,
  useSkillVerificationNotificationV2,
  useSkillUser,
  useUpdateRecommededSkill,
  useUpdateVerifySkill,
  useGetSkillRecommendations,
  useVerifySkill,
};
